import { useQuery } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface JourneyDto {
  id: string;
  user_id: string;
  start_at: string;
  end_at: string;
  point_count: number;
  distance_m: number;
  // 'matched'  — OSRM map-matched to the road network. Accurate.
  // 'polyline' — straight lines between GPS samples (under-counts on curves).
  //              Surfaced as an "≈" prefix in the UI so operators know.
  distance_source: 'matched' | 'polyline';
  duration_seconds: number;
  start_lat: number | null;
  start_lng: number | null;
  end_lat: number | null;
  end_lng: number | null;
  battery_min: number | null;
  battery_max: number | null;
}

export interface JourneyPointDto {
  recorded_at: string;
  lat: number;
  lng: number;
  battery_pct: number | null;
  accuracy_m: number | null;
  speed_mps: number | null;
  source: string;
}

export function useJourneys(params: {
  userId?: string;
  from?: string;
  to?: string;
  gapMinutes?: number;
}) {
  const api = useApi();
  const { userId, from, to, gapMinutes } = params;
  return useQuery({
    queryKey: ['journeys', userId, from, to, gapMinutes],
    queryFn: () => {
      if (!userId || !from || !to) throw new Error('useJourneys: enabled gate broken');
      const qs = new URLSearchParams({ user_id: userId, from, to });
      if (gapMinutes) qs.set('gap_minutes', String(gapMinutes));
      return api<{ journeys: JourneyDto[] }>(`/journeys?${qs.toString()}`);
    },
    enabled: Boolean(userId && from && to),
    staleTime: 30_000,
  });
}

export function useJourneyPoints(params: { userId?: string; from?: string; to?: string }) {
  const api = useApi();
  const { userId, from, to } = params;
  return useQuery({
    queryKey: ['journeys', 'points', userId, from, to],
    queryFn: () => {
      if (!userId || !from || !to) throw new Error('useJourneyPoints: enabled gate broken');
      const qs = new URLSearchParams({ user_id: userId, from, to });
      return api<{ points: JourneyPointDto[] }>(`/journeys/points?${qs.toString()}`);
    },
    enabled: Boolean(userId && from && to),
    staleTime: 60_000,
  });
}
